import { useTranslation } from "react-i18next"

import { DataGuard } from "@/components/data-guard"
import { Page } from "@/components/page"
import {
  useActiveOrganization,
  useActiveOrganizationId,
  useAuthentication,
} from "@/contexts"
import { WYNN_ID } from "@/models/organization"
import { useFlags } from "@/services/feature"
import {
  allConsultantsWorkspaceName,
  allCustomersWorkspaceName,
  getActiveEnergyAndWaterUseWorkbookId,
  isEnergyAndWaterUseWorkbookId,
  useSigmaWorkbooks,
} from "@/services/sigma"
import type { SigmaWorkbook } from "@/services/sigma"

import { Box, Divider, Typography } from "@mui/material"

import { ReportGroup, reportCard, reportCards } from "../../models/report"
import type { ReportCard } from "../../models/report"
import { getCustomWorkbooksWorkspaceName } from "../../util/get-custom-workbooks-workspace-name"
import { ReportingCards } from "../reporting-cards"

const workbooksToReportCards = (
  workbooks: SigmaWorkbook[],
  group: ReportGroup
): ReportCard[] => {
  return workbooks.map((workbook) => {
    const matchedReportCard = reportCards.find(
      (wb) => wb.id === workbook.workbookId
    )

    return {
      buttonLink: workbook.workbookId,
      buttonText: "viewButton",
      description: matchedReportCard?.description,
      featureFlag: matchedReportCard?.featureFlag,
      group,
      title: matchedReportCard?.title ?? workbook.name,
    }
  })
}

const buildConsultantReportCards = (
  workbooks: SigmaWorkbook[],
  useCoreSchema: boolean | undefined,
  flagsFetched: boolean
): ReportCard[] => {
  const hasEnergyAndWaterUse = workbooks.some((workbook) =>
    isEnergyAndWaterUseWorkbookId(workbook.workbookId)
  )

  const otherCards = workbooks
    .filter((workbook) => !isEnergyAndWaterUseWorkbookId(workbook.workbookId))
    .map((workbook) => {
      const matchedReportCard = reportCards.find(
        (card) => card.id === workbook.workbookId
      )

      return {
        buttonLink: workbook.workbookId,
        buttonText: "viewButton",
        description: matchedReportCard?.description,
        featureFlag: matchedReportCard?.featureFlag,
        group: ReportGroup.Consultant,
        title: matchedReportCard?.title ?? workbook.name,
      }
    })

  if (!hasEnergyAndWaterUse || !flagsFetched) {
    return otherCards
  }

  const energyAndWaterUse = reportCard.EnergyAndWaterUse

  return [
    {
      buttonLink: getActiveEnergyAndWaterUseWorkbookId(Boolean(useCoreSchema)),
      buttonText: "viewButton",
      description: energyAndWaterUse.description,
      featureFlag: energyAndWaterUse.featureFlag,
      group: ReportGroup.Consultant,
      title: energyAndWaterUse.title,
    },
    ...otherCards,
  ]
}

export const Reporting: React.FC = () => {
  const { t } = useTranslation()
  const { isNZeroOSUser } = useAuthentication()
  const { activeOrganization } = useActiveOrganization()
  const { activeOrganizationId } = useActiveOrganizationId()
  const flags = useFlags()

  const isActiveOrganizationWynn = activeOrganizationId === WYNN_ID

  const customWorkbooksWorkspaceName = getCustomWorkbooksWorkspaceName(
    activeOrganizationId,
    activeOrganization?.name
  )

  const {
    sigmaWorkbooksData: customWorkbooksData,
    sigmaWorkbooksIsPlaceholderData: customWorkbooksIsPlaceholderData,
  } = useSigmaWorkbooks({
    apiOptions: {
      workspaceNames: [customWorkbooksWorkspaceName],
    },
  })

  const {
    sigmaWorkbooksData: consultantWorkbooksData,
    sigmaWorkbooksIsPlaceholderData: consultantWorkbooksIsPlaceholderData,
  } = useSigmaWorkbooks({
    apiOptions: {
      workspaceNames: [allConsultantsWorkspaceName],
    },
  })

  const {
    sigmaWorkbooksData: allCustomersWorkbooksData,
    sigmaWorkbooksIsPlaceholderData: allCustomersWorkbooksIsPlaceholderData,
  } = useSigmaWorkbooks({
    apiOptions: {
      placeholderCount: 0,
      workspaceNames: [allCustomersWorkspaceName],
    },
  })

  const customReportCards = workbooksToReportCards(
    customWorkbooksData?.entries ?? [],
    ReportGroup.Custom
  )

  const consultantReportCards = buildConsultantReportCards(
    consultantWorkbooksData?.entries ?? [],
    flags.use_core_schema,
    flags.flagsIsFetched
  )

  const allCustomersReportCards = workbooksToReportCards(
    allCustomersWorkbooksData?.entries ?? [],
    ReportGroup.One
  ).filter((card) => !card.featureFlag || flags[card.featureFlag])

  const group2ReportCards = reportCards.filter((card) => {
    return (
      card.group === ReportGroup.Two &&
      (!card.featureFlag || flags[card.featureFlag])
    )
  })

  return (
    <Page>
      <DataGuard>
        <Box
          sx={{
            mb: 2,
          }}
        >
          <Typography>{t("description", { ns: "reports" })}</Typography>
        </Box>
        {/* Custom reports */}
        {flags.report_custom_reports && Boolean(customReportCards.length) && (
          <>
            <ReportingCards
              cards={customReportCards}
              isLoading={customWorkbooksIsPlaceholderData}
            />
            {!isActiveOrganizationWynn && <Divider sx={{ marginBottom: 3 }} />}
          </>
        )}

        {/* NZeroOS reports */}
        {!isActiveOrganizationWynn &&
          isNZeroOSUser &&
          Boolean(consultantReportCards.length) && (
            <>
              <ReportingCards
                cards={consultantReportCards}
                isLoading={consultantWorkbooksIsPlaceholderData}
              />
              <Divider sx={{ marginBottom: 3 }} />
            </>
          )}

        {/* All users reports */}
        {!isActiveOrganizationWynn &&
          isNZeroOSUser &&
          Boolean(allCustomersReportCards.length) && (
            <ReportingCards
              cards={allCustomersReportCards}
              isLoading={allCustomersWorkbooksIsPlaceholderData}
            />
          )}

        {/* Group 2 reports */}
        {!isActiveOrganizationWynn && Boolean(group2ReportCards.length) && (
          <>
            <Divider sx={{ marginBottom: 3 }} />
            <ReportingCards cards={group2ReportCards} isLoading={false} />
          </>
        )}
      </DataGuard>
    </Page>
  )
}
